import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/lib/supabase";
import { getProductDetailUrl } from "@/lib/navigation-helpers";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import SupplierProfileForm from "@/polymet/components/admin/SupplierProfileForm";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
// import { useToast } from "@/components/ui/use-toast";
import { Separator } from "@/components/ui/separator";

// Import icons
import {
  Award,
  Building2,
  Briefcase,
  Calendar,
  CheckCircle,
  ExternalLink,
  FileText,
  Globe,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Star,
  Users,
  Package,
} from "lucide-react";

// Define interfaces for type safety
interface Account {
  id: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
  full_name?: string;
}


interface SupplierDocument {
  id: string;
  document_type: string;
  document_url: string;
  verified: boolean;
}

interface SupplierCertification {
  id: string;
  supplier_id: string;
  name: string;
  issuer?: string;
  issued_date?: string;
  expiry_date?: string;
  verification_status?: string;
  created_at?: string;
}

interface SupplierReview {
  id: string;
  supplier_id: string;
  reviewer_id: string;
  rating: number;
  review_text?: string;
  created_at?: string;
  accounts?: Account; // Reviewer account
}

interface SupplierProfile {
  id: string;
  slug?: string;
  business_name?: string;
  bio?: string;
  website?: string;
  logo_url?: string;
  accounts?: Account;
  supplier_documents?: SupplierDocument[];
  supplier_certifications?: SupplierCertification[];
  supplier_reviews?: SupplierReview[];
  location?: string;
  address?: string;
  rating?: number;
  review_count?: number;
  establishment_year?: number;
  legal_status?: string;
  nature_of_business?: string;
  annual_turnover?: string;
  employee_count?: string;
  import_export_code?: string;
  gst_number?: string;
  gst_registration_date?: string;
  pan_number?: string;
  trust_seal_verified?: boolean;
  machinesearch_certification?: boolean;
  payment_details?: any;
  main_markets?: string[];
  about?: string;
  mission?: string;
  vision?: string;
  joined_date?: string;
}

interface Product {
  id: string;
  name: string;
  slug?: string;
  sku?: string;
  category_id?: string;
  subcategory_id?: string;
  product_category_id?: string;
  condition: string;
  price: number;
  currency: string;
  description?: string;
  product_images?: Array<{
    image_url: string;
    is_primary?: boolean;
    display_order?: number;
  }>;
  images?: string[];
  year?: number;
  manufacturer?: string;
  model?: string;
  in_stock?: boolean;
  featured?: boolean;
}

interface Service {
  id: string;
  name: string;
  description: string;
  icon: string;
}

export default function SupplierProfilePage() {
  // Local state for user profile
  const [userProfile, setUserProfile] = useState<any>(null);
  // Get slug from URL params
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState("overview");
  // Navigation hook
  const navigate = useNavigate();

  const { user } = useAuth();
  // const { toast } = useToast();
  
  // State for supplier profile, documents, products, and services
  const [supplierProfile, setSupplierProfile] = useState<SupplierProfile | null>(null);

  // Fetch the user's profile to check admin status
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setUserProfile(null);
        return;
      }
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', user.id)
        .single();
      if (!error && data) {
        setUserProfile(data);
      } else {
        setUserProfile(null);
      }
    };
    fetchProfile();
  }, [user]);
  
  const [products, setProducts] = useState<Product[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  
  // State for contact and quote dialogs
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [quoteDetails, setQuoteDetails] = useState({
    quantity: '',
    requirements: '',
    deadline: '',
    additionalInfo: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Handler functions for contact and quote submissions
  const handleContactSubmit = async () => {
    try {
      setSubmitting(true);
      
      if (!user) {
        alert('You need to be logged in to contact suppliers.');
        return;
      }
      
      // Create a new inquiry in the database
      const { error } = await supabase.from('inquiries').insert({
        supplier_id: supplierProfile?.id,
        buyer_id: user.id,
        message: contactMessage,
        status: 'pending',
        type: 'contact'
      });
      
      if (error) {
        console.error('Error sending contact message:', error);
        alert('Failed to send message. Please try again.');
        return;
      }
      
      // Show success message
      alert('Your message has been sent to the supplier!');
      
      // Reset form and close dialog
      setContactMessage('');
      setContactDialogOpen(false);
    } catch (error) {
      console.error('Error in handleContactSubmit:', error);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleQuoteSubmit = async () => {
    try {
      setSubmitting(true);
      
      if (!user) {
        alert('You need to be logged in to request quotes.');
        return;
      }
      
      // Create a new quote request in the database
      const { error } = await supabase.from('inquiries').insert({
        supplier_id: supplierProfile?.id,
        buyer_id: user.id,
        message: JSON.stringify(quoteDetails),
        status: 'pending',
        type: 'quote'
      });
      
      if (error) {
        console.error('Error sending quote request:', error);
        alert('Failed to send quote request. Please try again.');
        return;
      }
      
      // Show success message
      alert('Your quote request has been sent to the supplier!');
      
      // Reset form and close dialog
      setQuoteDetails({
        quantity: '',
        requirements: '',
        deadline: '',
        additionalInfo: ''
      });
      setQuoteDialogOpen(false);
    } catch (error) {
      console.error('Error in handleQuoteSubmit:', error);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchSupplierProfileAndData = async () => {
      try {
        setLoading(true);
        
        // Make sure slug is defined before querying
        if (!slug) {
          setLoading(false);
          return;
        }

        // Fetch supplier profile by slug with related data using joins
        const { data: profile, error } = await supabase
          .from('supplier_profiles')
          .select(`
            *,
            accounts(id, email, phone, avatar_url),
            supplier_documents(*),
            supplier_certifications(*),
            supplier_reviews(id, rating, review_text, created_at, reviewer_id, accounts:reviewer_id(id, email, avatar_url))
          `)
          .eq('slug', slug)
          .single();

        if (error) {
          console.error('Error fetching supplier profile:', error);
          setLoading(false);
          return;
        }

        if (profile) {
          // Set the profile data directly
          setSupplierProfile(profile);
          
          // We already have supplier documents from the join
          

          // Fetch supplier products
          const { data: productsData, error: productsError } = await supabase
            .from("products")
            .select(`
              *,
              product_images(image_url, is_primary, display_order)
            `)
            .eq("supplier_id", profile.id);
            
          if (productsError) {
            console.error('Error fetching supplier products:', productsError);
          }

          // Only use real products from the database
          if (productsData) {
            // Transform products to include images array for backward compatibility
            const transformedProducts = productsData.map(product => ({
              ...product,
              images: product.product_images?.map((img: { image_url: string }) => img.image_url) || []
            }));
            setProducts(transformedProducts);
          }
          
          // Note: supplier_services table doesn't exist yet
          // When it's created, we can uncomment the following code:
          // try {
          //   const { data: servicesData, error: servicesError } = await supabase
          //     .from("supplier_services")
          //     .select("*")
          //     .eq("supplier_id", profile.id);
          //     
          //   if (servicesError) {
          //     console.error('Error fetching supplier services:', servicesError);
          //   }
          //   
          //   if (servicesData) {
          //     setServices(servicesData);
          //   }
          // } catch (error) {
          //   console.error('Error in services fetch:', error);
          // }
          
          setLoading(false);
        }
      } catch (error) {
        console.error('Error in fetchSupplierProfileAndData:', error);
        setLoading(false);
      }
    };

    if (slug) {
      fetchSupplierProfileAndData();
    }
  }, [slug]);

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }
  
  if (!supplierProfile) {
    return <div className="container mx-auto px-4 py-8">Supplier not found.</div>;
  }
  
  // Prepare data for display - only using actual data from Supabase
  const safeSupplier = {
    id: supplierProfile.id,
    company: supplierProfile.business_name,
    bio: supplierProfile.bio,
    website: supplierProfile.website,
    logo: supplierProfile.logo_url,
    email: supplierProfile.accounts?.email,
    phone: supplierProfile.accounts?.phone,
    location: supplierProfile.location,
    address: supplierProfile.address,
    memberSince: supplierProfile.joined_date ? new Date(supplierProfile.joined_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : null,
    establishedYear: supplierProfile.establishment_year,
    rating: supplierProfile.rating,
    reviewCount: supplierProfile.review_count || supplierProfile.supplier_reviews?.length || 0,
    // Business details
    legalStatus: supplierProfile.legal_status,
    natureOfBusiness: supplierProfile.nature_of_business,
    annualTurnover: supplierProfile.annual_turnover,
    employeeCount: supplierProfile.employee_count,
    importExportCode: supplierProfile.import_export_code,
    // Registration details
    gstNumber: supplierProfile.gst_number,
    gstRegistrationDate: supplierProfile.gst_registration_date ? new Date(supplierProfile.gst_registration_date).toLocaleDateString('en-US') : null,
    panNumber: supplierProfile.pan_number,
    // Verification
    trustSealVerified: supplierProfile.trust_seal_verified,
    machinesearchCertification: supplierProfile.machinesearch_certification,
    // Other details
    paymentDetails: supplierProfile.payment_details,
    certifications: supplierProfile.supplier_certifications?.map(cert => ({
      name: cert.name,
      issuer: cert.issuer,
      issuedDate: cert.issued_date ? new Date(cert.issued_date).toLocaleDateString() : null,
      expiryDate: cert.expiry_date ? new Date(cert.expiry_date).toLocaleDateString() : null,
      verificationStatus: cert.verification_status
    })) || [],
    reviews: supplierProfile.supplier_reviews || [],
    mainMarkets: supplierProfile.main_markets,
    about: supplierProfile.about,
    mission: supplierProfile.mission,
    vision: supplierProfile.vision
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Supplier Header - Company Details Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-shrink-0">
            {safeSupplier.logo ? (
              <img 
                src={safeSupplier.logo} 
                alt={safeSupplier?.company || "Supplier Logo"} 
                className="h-32 w-32 object-contain border rounded-lg"
              />
            ) : (
              <Avatar className="h-32 w-32 border-2 border-primary">
                <AvatarImage src="" alt={safeSupplier?.company || "Supplier Logo"} />
                <AvatarFallback>{safeSupplier?.company ? safeSupplier.company.charAt(0) : "S"}</AvatarFallback>
              </Avatar>
            )}
          </div>

          <div className="flex-grow">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-2">
                  {safeSupplier?.company || "Supplier"}
                  <Badge
                    variant="outline"
                    className="ml-2 bg-green-50 text-green-700 border-green-200"
                  >
                    <CheckCircle className="h-3 w-3 mr-1" /> Verified Supplier
                  </Badge>
                </h1>
                <p className="text-muted-foreground mt-1">{safeSupplier?.bio || "No description provided."}</p>
              </div>

              <div className="flex gap-2">
                <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                  <DialogTrigger asChild>
                    <Button disabled={!user} title={!user ? "Please log in to contact suppliers" : undefined}>Contact Supplier</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Contact {safeSupplier.company}</DialogTitle>
                      <DialogDescription>
                        Send a message to this supplier. They will respond to your inquiry via email.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid gap-2">
                        <Label htmlFor="message">Your Message</Label>
                        <Textarea
                          id="message"
                          placeholder="Enter your message here..."
                          value={contactMessage}
                          onChange={(e) => setContactMessage(e.target.value)}
                          className="min-h-[120px]"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" onClick={handleContactSubmit} disabled={submitting || !contactMessage.trim() || !user}>
                        {submitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                
                <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" disabled={!user} title={!user ? "Please log in to request quotes" : undefined}>Request Quote</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[525px]">
                    <DialogHeader>
                      <DialogTitle>Request Quote from {safeSupplier.company}</DialogTitle>
                      <DialogDescription>
                        Fill in the details to request a quote for products or services.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid gap-2">
                        <Label htmlFor="quantity">Quantity Required</Label>
                        <Input
                          id="quantity"
                          placeholder="e.g., 10 units"
                          value={quoteDetails.quantity}
                          onChange={(e) => setQuoteDetails({...quoteDetails, quantity: e.target.value})}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="requirements">Product/Service Requirements</Label>
                        <Textarea
                          id="requirements"
                          placeholder="Describe the product or service you need..."
                          value={quoteDetails.requirements}
                          onChange={(e) => setQuoteDetails({...quoteDetails, requirements: e.target.value})}
                          className="min-h-[80px]"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="deadline">Required By</Label>
                        <Input
                          id="deadline"
                          type="date"
                          value={quoteDetails.deadline}
                          onChange={(e) => setQuoteDetails({...quoteDetails, deadline: e.target.value})}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="additionalInfo">Additional Information</Label>
                        <Textarea
                          id="additionalInfo"
                          placeholder="Any other details that might help the supplier..."
                          value={quoteDetails.additionalInfo}
                          onChange={(e) => setQuoteDetails({...quoteDetails, additionalInfo: e.target.value})}
                          className="min-h-[80px]"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" onClick={handleQuoteSubmit} disabled={submitting || !quoteDetails.requirements.trim() || !user}>
                        {submitting ? 'Submitting...' : 'Submit Request'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>{safeSupplier.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>Established {safeSupplier.establishedYear}</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-amber-500" />
                <span>
                  {safeSupplier.rating} ({safeSupplier.reviewCount} reviews)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                {safeSupplier.website ? (
                  <a href={safeSupplier.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center">
                    Visit Website <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                ) : (
                  <span>No website available</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs
        defaultValue="overview"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-8">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="about">About Us</TabsTrigger>
          <TabsTrigger value="products">Products & Services</TabsTrigger>
          <TabsTrigger value="contact">Contact</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-8">
              {/* Company Overview */}
              <Card>
                <CardHeader>
                  <CardTitle>Company Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">About Us</h3>
                    <p className="text-muted-foreground">
                      {safeSupplier.about}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <h3 className="font-semibold mb-2">Our Mission</h3>
                      <p className="text-muted-foreground">
                        {safeSupplier.mission}
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Our Vision</h3>
                      <p className="text-muted-foreground">
                        {safeSupplier.vision}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h3 className="font-semibold mb-3">Business Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
                        <Building2 className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-medium">Nature of Business</div>
                          <div className="text-sm">{safeSupplier.natureOfBusiness}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
                        <Briefcase className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-medium">Legal Status</div>
                          <div className="text-sm">{safeSupplier.legalStatus}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
                        <Calendar className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-medium">Year Established</div>
                          <div className="text-sm">{safeSupplier.establishedYear}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
                        <Users className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-medium">Total Employees</div>
                          <div className="text-sm">{safeSupplier.employeeCount}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
                        <FileText className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-medium">Annual Turnover</div>
                          <div className="text-sm">{safeSupplier.annualTurnover}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
                        <Globe className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-medium">Main Markets</div>
                          <div className="text-sm">{safeSupplier.mainMarkets?.join(', ')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h3 className="font-semibold mb-3">Registration Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
                        <FileText className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-medium">GST Number</div>
                          <div className="text-sm">{safeSupplier.gstNumber}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
                        <Calendar className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-medium">GST Registration Date</div>
                          <div className="text-sm">{safeSupplier.gstRegistrationDate}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
                        <FileText className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-medium">PAN Number</div>
                          <div className="text-sm">{safeSupplier.panNumber}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
                        <Globe className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-medium">Import Export Code</div>
                          <div className="text-sm">{safeSupplier.importExportCode}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h3 className="font-semibold mb-3">Verification Status</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {safeSupplier.trustSealVerified && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                          <CheckCircle className="h-5 w-5 text-green-500" />
                          <div>
                            <div className="text-sm font-medium">Trust Seal Verified</div>
                            <div className="text-sm text-muted-foreground">This supplier has been verified by our Trust Seal program</div>
                          </div>
                        </div>
                      )}
                      {safeSupplier.machinesearchCertification && (
                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                          <Award className="h-5 w-5 text-amber-500" />
                          <div>
                            <div className="text-sm font-medium">MachineSearch Certified</div>
                            <div className="text-sm text-muted-foreground">This supplier has been verified by MachineSearch</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Featured Products */}
              <Card>
                <CardHeader>
                  <CardTitle>Featured Products</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {products.filter(product => product.featured).slice(0, 3).map((product) => (
                      <div
                        key={product.id}
                        className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(getProductDetailUrl(product.slug || product.id))}
                      >
                        <div className="aspect-video bg-muted">
                          <img
                            src={product.images && product.images.length > 0 ? product.images[0] : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200" viewBox="0 0 400 200"%3E%3Crect width="400" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="16"%3ENo Image%3C/text%3E%3C/svg%3E'}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div className="p-3">
                          <h3 className="font-medium text-sm line-clamp-2">
                            {product.name}
                          </h3>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-sm font-bold">
                              {product.currency}{" "}
                              {product.price.toLocaleString()}
                            </span>
                            <Badge
                              variant={
                                product.condition === "New"
                                  ? "default"
                                  : "secondary"
                              }
                            >
                              {product.condition}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-center">
                    <Button
                      variant="outline"
                      onClick={() => setActiveTab("products")}
                    >
                      View All Products
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-8">
              {/* Quick Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Business Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <div className="font-medium">Business Type</div>
                      <div className="text-muted-foreground">
                        Manufacturer & Exporter
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <div className="font-medium">Number of Employees</div>
                      <div className="text-muted-foreground">{safeSupplier.employeeCount} People</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Globe className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <div className="font-medium">Main Markets</div>
                      <div className="text-muted-foreground">
                        {Array.isArray(safeSupplier.mainMarkets) ? safeSupplier.mainMarkets.join(', ') : safeSupplier.mainMarkets}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <div className="font-medium">GST Number</div>
                      <div className="text-muted-foreground">
                        {safeSupplier.paymentDetails?.gstNumber || 'Not provided'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Certifications */}
              <Card>
                <CardHeader>
                  <CardTitle>Certifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {safeSupplier.certifications.map((cert, index) => (
                      <div key={index} className="flex items-center gap-3 border rounded-md p-3">
                        <Award className="h-5 w-5 text-primary" />
                        <div>
                          <div className="font-medium">{cert.name}</div>
                          <div className="text-xs text-muted-foreground">
                            Issued by {cert.issuer || 'Unknown'} • {cert.expiryDate ? `Valid until ${new Date(cert.expiryDate).toLocaleDateString()}` : 'No expiry'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* Customer Reviews */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Customer Reviews</CardTitle>
                  <CardDescription>Based on {safeSupplier.reviewCount} reviews</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {safeSupplier.reviews.length > 0 ? (
                      safeSupplier.reviews.map((review: SupplierReview, index: number) => (
                        <div key={index} className="border rounded-md p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Avatar className="w-12 h-12">
                                {review.accounts?.avatar_url ? (
                                  <AvatarImage src={review.accounts.avatar_url} alt="Reviewer" />
                                ) : (
                                  <AvatarFallback>{review.accounts?.email?.charAt(0).toUpperCase() || 'U'}</AvatarFallback>
                                )}
                              </Avatar>
                              <div>
                                <div className="font-medium">{review.accounts?.email || 'Anonymous User'}</div>
                                <div className="text-xs text-muted-foreground">
                                  {review.created_at ? new Date(review.created_at).toLocaleDateString() : 'Unknown date'}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${i < review.rating ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground'}`}
                                />
                              ))}
                            </div>
                          </div>
                          {review.review_text && (
                            <p className="text-sm mt-2">{review.review_text}</p>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-20" />
                        <p>No reviews yet</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* About Us Tab */}
        <TabsContent value="about" className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>About {safeSupplier.company}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">Company Profile</h3>
                <p className="text-muted-foreground">
                  {safeSupplier.about}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3">Our Mission</h3>
                  <p className="text-muted-foreground">
                    {safeSupplier.mission}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold mb-3">Our Vision</h3>
                  <p className="text-muted-foreground">
                    {safeSupplier.vision}
                  </p>
                </div>
              </div>
              
              <Separator className="my-6" />
              
              <div>
                <h3 className="text-xl font-semibold mb-3">Company History</h3>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="font-medium w-16">{safeSupplier.establishedYear || 'N/A'}</div>
                    <div className="text-muted-foreground">
                      Founded in {safeSupplier.location ? safeSupplier.location.split(',')[0] : 'Unknown Location'}
                    </div>
                  </div>
                  {safeSupplier.establishedYear && (
                    <>
                      <div className="flex gap-4">
                        <div className="font-medium w-16">{safeSupplier.establishedYear + 4}</div>
                        <div className="text-muted-foreground">
                          Expanded operations to new markets
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="font-medium w-16">{safeSupplier.establishedYear + 7}</div>
                        <div className="text-muted-foreground">
                          Received ISO 9001 certification
                        </div>
                      </div>
                    </>
                  )}
                  
                  {safeSupplier.establishedYear && (
                    <div className="flex gap-4">
                      <div className="font-medium w-16">{safeSupplier.establishedYear + 10}</div>
                      <div className="text-muted-foreground">
                        Started exporting to international markets
                      </div>
                    </div>
                  )}
                  {safeSupplier.establishedYear && (
                    <div className="flex gap-4">
                      <div className="font-medium w-16">{safeSupplier.establishedYear + 13}</div>
                      <div className="text-muted-foreground">
                        Launched new line of eco-friendly machinery
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <Separator className="my-6" />
              
              <div>
                <h3 className="text-xl font-semibold mb-3">Business Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">Business Type</div>
                      <div className="text-muted-foreground">
                        Manufacturer & Exporter
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">Number of Employees</div>
                      <div className="text-muted-foreground">{safeSupplier.employeeCount} People</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Globe className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">Main Markets</div>
                      <div className="text-muted-foreground">
                        {Array.isArray(safeSupplier.mainMarkets) ? safeSupplier.mainMarkets.join(', ') : safeSupplier.mainMarkets}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">Year Established</div>
                      <div className="text-muted-foreground">
                        {safeSupplier.establishedYear}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Certifications & Compliance</CardTitle>
              <CardDescription>
                Our business adheres to international standards and has obtained various certifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {safeSupplier.certifications && safeSupplier.certifications.map((cert, index) => (
                  <Card key={index} className="overflow-hidden border">
                    <div className="p-4 flex items-start gap-3">
                      <Award className="h-8 w-8 text-primary" />
                      <div>
                        <h3 className="font-semibold">{cert.name}</h3>
                        <p className="text-muted-foreground text-sm">
                          Issued by {cert.issuer}
                        </p>
                        <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                          <span>Issued: {cert.issuedDate ? new Date(cert.issuedDate).toLocaleDateString() : "N/A"}</span>
                          <span>Valid until: {cert.expiryDate ? new Date(cert.expiryDate).toLocaleDateString() : "N/A"}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Products & Services Tab */}
        <TabsContent value="products" className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Our Products</CardTitle>
              <CardDescription>
                Browse our comprehensive range of high-quality industrial machinery and equipment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {products.length > 0 ? (
                  products.map((product) => (
                    <div
                      key={product.id}
                      className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(getProductDetailUrl(product.slug || product.id))}
                    >
                      <div className="aspect-video bg-muted">
                        <img
                          src={product.images && product.images.length > 0 ? product.images[0] : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200" viewBox="0 0 400 200"%3E%3Crect width="400" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="16"%3ENo Image%3C/text%3E%3C/svg%3E'}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-medium line-clamp-2 mb-1">{product.name}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                          {product.description}
                        </p>
                        <div className="flex justify-between items-center">
                          <span className="font-bold">
                            {product.currency} {product.price.toLocaleString()}
                          </span>
                          <Badge variant={product.condition === "New" ? "default" : "secondary"}>
                            {product.condition}
                          </Badge>
                        </div>
                        <Button 
                          className="w-full mt-3" 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(getProductDetailUrl(product.slug || product.id));
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-4 text-center py-8">
                    <p className="text-muted-foreground">No details found in the table.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Our Services</CardTitle>
              <CardDescription>
                We offer a range of professional services to support your business needs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {services.length > 0 ? (
                  services.map((service: Service) => (
                    <Card key={service.id} className="border">
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                          <div className="bg-primary/10 p-3 rounded-full">
                            {service.icon === 'briefcase' && <Briefcase className="h-6 w-6 text-primary" />}
                            {service.icon === 'tool' && <FileText className="h-6 w-6 text-primary" />}
                            {service.icon === 'package' && <Package className="h-6 w-6 text-primary" />}
                            {service.icon === 'users' && <Users className="h-6 w-6 text-primary" />}
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg mb-1">{service.name}</h3>
                            <p className="text-muted-foreground">
                              {service.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="col-span-2 text-center py-8">
                    <p className="text-muted-foreground">No services found in the table.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Tab */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>
                Get in touch with our sales and support team
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">Email</div>
                      <div className="text-muted-foreground">
                        {safeSupplier.email}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">Phone</div>
                      <div className="text-muted-foreground">
                        {safeSupplier.phone}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">Address</div>
                      <div className="text-muted-foreground">
                        {safeSupplier.company || 'Company'} Headquarters<br />
                        123 Industrial Area, Phase 2<br />
                        {safeSupplier.location}
                      </div>
                    </div>
                  </div>
                  
                  {safeSupplier.website && (
                    <div className="flex items-start gap-3">
                      <Globe className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <div className="font-medium">Website</div>
                        <div className="text-muted-foreground">
                          <a href={safeSupplier.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            {safeSupplier.website}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4">Business Hours</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Monday - Friday</span>
                        <span>9:00 AM - 6:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Saturday</span>
                        <span>10:00 AM - 4:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sunday</span>
                        <span>Closed</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <Card className="bg-muted/50">
                    <CardHeader>
                      <CardTitle className="text-base">Send us a message</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">First Name</label>
                            <input type="text" className="w-full p-2 border rounded-md" placeholder="Your first name" />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Last Name</label>
                            <input type="text" className="w-full p-2 border rounded-md" placeholder="Your last name" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Email</label>
                          <input type="email" className="w-full p-2 border rounded-md" placeholder="your.email@example.com" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Phone</label>
                          <input type="tel" className="w-full p-2 border rounded-md" placeholder="Your phone number" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Message</label>
                          <textarea className="w-full p-2 border rounded-md h-24" placeholder="How can we help you?"></textarea>
                        </div>
                        <Button className="w-full">Send Message</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      {/* Admin Edit/Add Supplier Profile Button and Dialog */}
      {userProfile?.is_admin && (
        <div className="mb-4 flex justify-end">
          <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
            {supplierProfile ? "Edit Supplier Profile" : "Add Supplier Profile"}
          </Button>
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{supplierProfile ? "Edit Supplier Profile" : "Add Supplier Profile"}</DialogTitle>
                <DialogDescription>
                  {supplierProfile
                    ? "Update the supplier's details below."
                    : "Fill out the form to create a new supplier profile."}
                </DialogDescription>
              </DialogHeader>
              <SupplierProfileForm
                supplierId={supplierProfile?.id || undefined}
                onChange={() => {
                  setEditDialogOpen(false);
                  // Optionally, refetch supplier profile here
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
      )}
      </Tabs>
    </div>
  );
}
